import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { get, omit } from 'lodash';
import classnames from 'classnames';
import { AdUnit } from 'site-modules/shared/components/ad-unit/ad-unit';
import { updateVehicleByStyleId } from 'client/actions/vehicle';
import { queryToSearch } from 'client/utils/query-to-search';
import { getQuery } from 'client/utils/location';
import { venomHistory } from 'client/utils/history/venom-history';
import { parseVehicleParam } from 'client/utils/parse-vehicle-param';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { VehicleEntities, VehicleModel } from 'client/data/models/vehicle-v2';
import { normalizeQueryOrder } from 'site-modules/comparator/utils/query-handler';
import { delayHeavyTask } from 'site-modules/shared/utils/inp-utils';

// Constants
import {
  COMPARATOR_PATHNAME,
  MAX_COMPARE_SIZE,
  NAVIGATION_MENU_HEIGHT,
  VEHICLE_PATH_NAME,
} from 'site-modules/comparator/constants/comparator';

// Components
import { FloodlightAd } from 'site-modules/shared/components/floodlight/floodlight-ad';
import { comparatorGridStickyScroll } from 'site-modules/comparator/components/comparator-grid-sticky-scroll/comparator-grid-sticky-scroll';
import { ComparatorTable } from 'site-modules/comparator/components/comparator-table/comparator-table';
import { SponsoredCars } from 'site-modules/shared/components/core-page/sponsored-cars/sponsored-cars';
import { MinHeightWrapper } from 'site-modules/shared/components/min-height-wrapper/min-height-wrapper';

import './comparator-grid.scss';

export function ComparatorGridContent({
  veh1,
  veh2,
  veh3,
  veh4,
  title,
  columns,
  navList,
  sponsoredContent,
  deviceName,
  onStickyStateChange,
  comparatorHeaderRef,
  comparatorHeaderStickyRef,
  setCustomOffset,
  customOffset,
  isMobile,
  hasComparisonContent,
  onUpdateVehicle,
  location,
  isMMvsMMComparator,
  hideSimilar,
  enableAds10208,
}) {
  const sponsoredColumnRemoved = useRef(false);
  const styles = [veh1, veh2, veh3, veh4];

  const queryForMMvsMMComparator = useMemo(
    () => ({
      [`${VEHICLE_PATH_NAME}1`]: veh1 && veh1.id,
      [`${VEHICLE_PATH_NAME}2`]: veh2 && veh2.id,
    }),
    [veh1, veh2]
  );

  const removeVehicle = useCallback(
    ({ name }) => {
      const query = isMMvsMMComparator ? queryForMMvsMMComparator : getQuery(location);
      const newQuery = omit(query, name);
      const newState = normalizeQueryOrder(newQuery, COMPARATOR_PATHNAME);

      if (name === 'sponsor') {
        sponsoredColumnRemoved.current = true;
      }

      if (query.veh1 !== newQuery.veh1) {
        onUpdateVehicle(newState.query.veh1);
      }

      delayHeavyTask(() => {
        venomHistory.replace({
          ...newState,
          search: queryToSearch(newState.query),
          query: undefined,
        });
      });
    },
    [isMMvsMMComparator, location, onUpdateVehicle, queryForMMvsMMComparator]
  );

  const addVehicle = useCallback(
    ({ styleId, name }) => {
      const query = getQuery(location);
      let newName = null;

      if (name === 'sponsor') {
        removeVehicle({ name });
        newName = `${VEHICLE_PATH_NAME}${MAX_COMPARE_SIZE}`;
      }

      const newQuery = {
        ...query,
        ...(isMMvsMMComparator && queryForMMvsMMComparator),
        [newName || name]: styleId,
      };

      const newState = {
        pathname: COMPARATOR_PATHNAME,
        search: queryToSearch(newQuery),
      };

      if (query.veh1 !== newQuery.veh1) {
        onUpdateVehicle(newQuery.veh1);
      }

      delayHeavyTask(() => {
        venomHistory.replace(newState);
      });
    },
    [isMMvsMMComparator, location, onUpdateVehicle, queryForMMvsMMComparator, removeVehicle]
  );

  useEffect(() => {
    if (comparatorHeaderRef.current) {
      setCustomOffset(comparatorHeaderRef.current.clientHeight + NAVIGATION_MENU_HEIGHT);
    }
  });

  const hasSponsoredColumn = sponsoredContent && !sponsoredColumnRemoved.current;
  const columnsWithSelection = columns.filter(column => column.styleId).length;
  const shouldRenderSelected = !!columnsWithSelection || hasSponsoredColumn;
  const firstColumnVehicle = get(columns, '[0]', {});
  const shouldRenderFloodlight = !!firstColumnVehicle.styleId;

  if (hasSponsoredColumn) {
    const sponsoredQuery = {
      sponsor: sponsoredContent.styleId,
    };
    const sponsoredColumn = {
      ...parseVehicleParam(sponsoredQuery, 'sponsor'),
      ...get(sponsoredContent, `${deviceName}`),
    };
    sponsoredColumn.oemLogoSrc = get(sponsoredContent, 'oemLogoSrc');
    sponsoredColumn.isSponsored = true;

    if (sponsoredColumn.styleId) {
      // eslint-disable-next-line no-param-reassign
      columns[columns.length - 1] = sponsoredColumn;
    }
  }

  return (
    <div className={classnames('car-comparator-grid', { sponsored: hasSponsoredColumn })}>
      <ComparatorTable
        columns={columns}
        styles={styles}
        navList={navList}
        customOffset={customOffset}
        addVehicle={addVehicle}
        removeVehicle={removeVehicle}
        onStickyStateChange={onStickyStateChange}
        title={title}
        sponsoredContent={get(sponsoredContent, `${deviceName}`)}
        shouldRenderSelected={shouldRenderSelected}
        comparatorHeaderRef={comparatorHeaderRef}
        comparatorHeaderStickyRef={comparatorHeaderStickyRef}
        isSmall={isMobile}
        hasComparisonContent={hasComparisonContent}
        hideSimilar={hideSimilar}
        enableAds10208={enableAds10208}
      />
      {hasSponsoredColumn && (
        <AdUnit adName="compare" position={'1'} all renderWhenViewable={false} refreshable={false} />
      )}
      {shouldRenderFloodlight && <FloodlightAd />}
      {styles[0] && (
        <div className="mt-1">
          <MinHeightWrapper componentType="SPONSORED_CARS">
            <SponsoredCars />
          </MinHeightWrapper>
        </div>
      )}
    </div>
  );
}

ComparatorGridContent.propTypes = {
  onStickyStateChange: PropTypes.func.isRequired,
  comparatorHeaderRef: PropTypes.shape().isRequired,
  comparatorHeaderStickyRef: PropTypes.shape().isRequired,
  onUpdateVehicle: PropTypes.func.isRequired,
  setCustomOffset: PropTypes.func.isRequired,
  deviceName: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      styleId: PropTypes.string,
    })
  ).isRequired,
  veh1: VehicleEntities.Style,
  veh2: VehicleEntities.Style,
  veh3: VehicleEntities.Style,
  veh4: VehicleEntities.Style,
  navList: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, hash: PropTypes.string })).isRequired,
  customOffset: PropTypes.number,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }),
  sponsoredContent: PropTypes.shape({
    styleId: PropTypes.string,
    oemLogoSrc: PropTypes.string,
    mobile: PropTypes.shape({
      oemLogoLink: PropTypes.string,
      oemYmmLink: PropTypes.string,
      oemGetStartedLink: PropTypes.string,
      oemDealerInventoryLink: PropTypes.string,
      oemConsumerReviewLink: PropTypes.string,
      oemEdmundsReviewLink: PropTypes.string,
    }),
    desktop: PropTypes.shape({
      oemLogoLink: PropTypes.string,
      oemYmmLink: PropTypes.string,
      oemGetStartedLink: PropTypes.string,
      oemDealerInventoryLink: PropTypes.string,
      oemConsumerReviewLink: PropTypes.string,
      oemEdmundsReviewLink: PropTypes.string,
    }),
  }),
  isMobile: PropTypes.bool,
  hideSimilar: PropTypes.bool,
  isMMvsMMComparator: PropTypes.bool,
  hasComparisonContent: PropTypes.bool,
  enableAds10208: PropTypes.bool,
};

ComparatorGridContent.defaultProps = {
  location: {
    search: '',
    pathname: '',
  },
  veh1: null,
  veh2: null,
  veh3: null,
  veh4: null,
  customOffset: 0,
  sponsoredContent: null,
  isMobile: false,
  hideSimilar: false,
  isMMvsMMComparator: false,
  hasComparisonContent: true,
  enableAds10208: false,
};

const stateToPropsConfig = {
  veh1: bindToPath(({ columns }) => columns[0].styleId && `styles.${columns[0].styleId}`, VehicleModel),
  veh2: bindToPath(({ columns }) => columns[1].styleId && `styles.${columns[1].styleId}`, VehicleModel),
  veh3: bindToPath(({ columns }) => columns[2].styleId && `styles.${columns[2].styleId}`, VehicleModel),
  veh4: bindToPath(({ columns }) => columns[3].styleId && `styles.${columns[3].styleId}`, VehicleModel),
};

export const ComparatorGrid = connect(null, { onUpdateVehicle: updateVehicleByStyleId })(
  connectToModel(comparatorGridStickyScroll(ComparatorGridContent), stateToPropsConfig)
);
